import React, { useEffect, useRef } from 'react';
import { Box, Grid, Text, Flex } from '@chakra-ui/react';
import { motion, useReducedMotion, useInView, useMotionValue, animate } from 'framer-motion';

const MotionBox = motion(Box);

function CountUp({ target, prefix = '', suffix = '', duration = 1.8 }) {
  const shouldReduce = useReducedMotion();
  const ref       = useRef(null);
  const dispRef   = useRef(null);
  const motionVal = useMotionValue(shouldReduce ? target : 0);
  const isInView  = useInView(ref, { once: true, margin: '-60px' });

  useEffect(() => {
    if (!isInView) return;
    if (shouldReduce) {
      if (dispRef.current) dispRef.current.textContent = `${prefix}${target}${suffix}`;
      return;
    }
    const ctrl = animate(motionVal, target, {
      duration,
      ease: 'easeOut',
      onUpdate: v => { if (dispRef.current) dispRef.current.textContent = `${prefix}${Math.round(v)}${suffix}`; },
    });
    return () => ctrl.stop();
  }, [isInView, shouldReduce, target, duration, prefix, suffix, motionVal]);

  return (
    <Text ref={ref} as="span" fontWeight={700} lineHeight={1} color="oid.text"
      sx={{ fontSize: 'clamp(32px, 4.5vw, 56px)' }}
      aria-label={`${prefix}${target}${suffix}`}>
      <span ref={dispRef} aria-hidden="true">
        {shouldReduce ? `${prefix}${target}${suffix}` : `${prefix}0${suffix}`}
      </span>
    </Text>
  );
}

const STATS = [
  { id: 'swap',   prefix: '',  value: 8,  suffix: '',  unit: 'min',
    accentPrefix: '<', label: 'per module swap' },
  { id: 'tools',  prefix: '',  value: 0,  suffix: '',  unit: '',
    label: 'specialist tools required' },
  { id: 'uptime', prefix: '',  value: 99, suffix: '%', unit: '',
    accentSuffix: '%', label: 'uptime target' },
];

const PRINCIPLES = [
  'Tool-free latching mechanism',
  'Embedded module identity chip',
  'Auto-calibration on attach',
  'Color-coded connector bands',
  'IP67-rated module seals',
];

function FieldService() {
  const shouldReduce = useReducedMotion();
  const ease = [0.25, 0.1, 0.25, 1];

  return (
    <Box as="section" id="field-service" aria-label="Field serviceability"
      bg="oid.bg" py={{ base: '55px', md: '89px' }}>
      <Grid templateColumns={{ base: '1fr', lg: '61.8% 38.2%' }}
        gap={{ base: 12, lg: 20 }} alignItems="start"
        maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>

        {/* Left — 61.8% */}
        <MotionBox
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease }}>
          <Box mb={6}>
            <Box as="span" display="block" width="24px" height="2px"
              bg="oid.accent" mb={4} aria-hidden="true" />
            <Text fontSize="11px" fontWeight={600} letterSpacing="0.3em"
              textTransform="uppercase" color="oid.accent">
              04. Field Serviceability
            </Text>
          </Box>
          <Text as="h2" fontWeight={700} lineHeight={1.1} color="oid.text"
            sx={{ fontSize: 'clamp(28px, 3.5vw, 48px)' }} mb={6}>
            No facility.<br/>No specialist.<br/>
            <Text as="span" color="oid.muted" fontWeight={400}>No downtime.</Text>
          </Text>
          <Text fontSize="1rem" fontWeight={400} color="oid.muted"
            lineHeight={1.618} maxWidth="500px" mb={10}>
            Every module was designed with field replacement as a primary requirement.
            Any trained operator can swap a failed unit in under 8 minutes using
            only tools already in a standard field kit.
          </Text>

          {/* Stats */}
          <Grid templateColumns={{ base: '1fr', sm: 'repeat(3, 1fr)' }}
            gap="1px" bg="oid.border" border="1px solid" borderColor="oid.border"
            aria-label="Key serviceability statistics">
            {STATS.map(stat => (
              <Box key={stat.id} bg="oid.bg" p={6} textAlign="center">
                <Flex align="baseline" justify="center" gap={1} mb={2}>
                  {stat.accentPrefix && (
                    <Text as="span" fontWeight={700} color="oid.accent" aria-hidden="true"
                      sx={{ fontSize: 'clamp(22px, 3vw, 36px)' }}>{stat.accentPrefix}</Text>
                  )}
                  <CountUp target={stat.value} prefix={stat.prefix}
                    suffix={stat.accentSuffix ? '' : stat.suffix} />
                  {stat.accentSuffix && (
                    <Text as="span" fontWeight={700} color="oid.accent" aria-hidden="true"
                      sx={{ fontSize: 'clamp(22px, 3vw, 36px)' }}>{stat.accentSuffix}</Text>
                  )}
                  {stat.unit && (
                    <Text as="span" fontSize="sm" fontWeight={400} color="oid.muted" ml={1}>
                      {stat.unit}
                    </Text>
                  )}
                </Flex>
                <Text fontSize="10px" fontWeight={600} letterSpacing="0.18em"
                  textTransform="uppercase" color="oid.muted">{stat.label}</Text>
              </Box>
            ))}
          </Grid>
        </MotionBox>

        {/* Right sidebar — 38.2% */}
        <MotionBox
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, delay: 0.2, ease }}>
          <Text fontSize="11px" fontWeight={600} letterSpacing="0.3em"
            textTransform="uppercase" color="oid.muted" mb={6}>
            Design Principles
          </Text>
          <Text fontSize="0.9375rem" fontWeight={400} color="oid.muted"
            lineHeight={1.618} mb={5}>
            Color-coded connectors eliminate guesswork in low-light or high-stress
            environments. Each module type has a unique color band, so even a first-time
            operator cannot install the wrong component.
          </Text>
          <Text fontSize="0.9375rem" fontWeight={400} color="oid.muted"
            lineHeight={1.618} mb={8}>
            Auto-calibration runs on every module attach event. The compute core
            handshakes with the new module's embedded chip, loads the driver and
            configuration, and confirms readiness with no operator intervention needed.
          </Text>
          <Box as="ul" listStyleType="none" m={0} p={0}
            borderTop="1px solid" borderColor="oid.border">
            {PRINCIPLES.map(item => (
              <Box as="li" key={item} py={3} borderBottom="1px solid"
                borderColor="oid.border" display="flex" alignItems="center" gap={3}>
                <Box width="5px" height="5px" borderRadius="full"
                  bg="oid.accent" flexShrink={0} aria-hidden="true" />
                <Text fontSize="xs" fontWeight={500} color="oid.text">{item}</Text>
              </Box>
            ))}
          </Box>
        </MotionBox>
      </Grid>
    </Box>
  );
}
export default FieldService;
